import { DataTableAsyncFilter } from '../DataTable/DataTableAsyncFilter';
import { DataTableBooleanFilter } from '../DataTable/DataTableBooleanFilter';
import { DataTableDateFilter } from '../DataTable/DataTableDateFilter';
import { DataTableDateRangeFilter } from '../DataTable/DataTableDateRangeFilter';
import { DataTableEntityFilter } from '../DataTable/DataTableEntityFilter';
import { DataTableFacetedFilter } from '../DataTable/DataTableFacetedFilter';
import { DataTableNumberRangeFilter } from '../DataTable/DataTableNumberRangeFilter';
import { DataTableTextFilter } from '../DataTable/DataTableTextFilter';

import type { ColumnFilterConfig, ExtraFilter } from './types';

/**
 * Traduit un filtre autonome vers la forme normalisée que `FilterChip` consomme. Les filtres de colonne
 * de la `DataTable` portent déjà cette forme dans `meta.filter`.
 */
export function buildExtraFilterConfig<TFilter>(extra: ExtraFilter): ColumnFilterConfig<TFilter> {
    const filterKey = extra.key as keyof TFilter;

    switch (extra.type) {
        case 'text':
        case 'number':
            return { type: extra.type, filterKey, placeholder: extra.placeholder };
        case 'select':
        case 'multi-select':
            return { type: extra.type, filterKey, options: extra.options, renderOption: extra.renderOption };
        case 'async-multi-select':
            return 'source' in extra
                ? { type: extra.type, filterKey, source: extra.source }
                : { type: extra.type, filterKey, loadOptions: extra.loadOptions };
        default:
            return { type: extra.type, filterKey };
    }
}

/** Un filtre alimenté par une source se nomme tout seul. */
export function getExtraFilterTitle(extra: ExtraFilter): string {
    return extra.title ?? ('source' in extra ? extra.source.labels.plural : extra.key);
}

type FilterChipProps<TFilter> = {
    config: ColumnFilterConfig<TFilter>;
    title: string;
    value: unknown;
    onChange: (value: unknown) => void;
};

/**
 * LE rendu d'un filtre. Unique : la barre d'outils de la `DataTable` et celle de la `DataView`
 * portaient chacune sa copie du même `switch` sur les mêmes huit widgets.
 *
 * Les valeurs de liste sont normalisées à l'entrée (une valeur scalaire venue d'une URL — `?state=Ready`
 * — est acceptée) et remises à `undefined` quand la sélection se vide, pour que la clé disparaisse de
 * l'état plutôt que d'y rester en tableau vide.
 */
export function FilterChip<TFilter>({ config, title, value, onChange }: FilterChipProps<TFilter>) {
    const selectedValues = Array.isArray(value) ? (value as string[]) : value ? [String(value)] : [];
    const onValuesChange = (values: string[]) => onChange(values.length > 0 ? values : undefined);

    switch (config.type) {
        case 'select':
        case 'multi-select':
            return (
                <DataTableFacetedFilter
                    title={title}
                    options={config.options || []}
                    selectedValues={selectedValues}
                    onChange={onValuesChange}
                    multi={config.type === 'multi-select'}
                    renderOption={config.renderOption}
                />
            );
        case 'async-multi-select':
            // Une source dispense la page d'écrire `loadOptions` ; celui-ci reste l'échappatoire pour
            // une liste qui ne vient pas d'une collection.
            if (config.source) {
                return (
                    <DataTableEntityFilter
                        source={config.source}
                        title={title}
                        selectedValues={selectedValues}
                        onChange={onValuesChange}
                    />
                );
            }

            return config.loadOptions ? (
                <DataTableAsyncFilter
                    title={title}
                    selectedValues={selectedValues}
                    onChange={onValuesChange}
                    loadOptions={config.loadOptions}
                />
            ) : null;
        case 'boolean':
            return <DataTableBooleanFilter title={title} value={value as boolean | undefined} onChange={onChange} />;
        case 'date':
            return (
                <DataTableDateFilter
                    title={title}
                    value={(value as string) || ''}
                    onChange={(newValue) => onChange(newValue || undefined)}
                />
            );
        case 'date-range':
            return (
                <DataTableDateRangeFilter
                    title={title}
                    value={value as { gte?: number; lte?: number } | undefined}
                    onChange={onChange}
                />
            );
        case 'number-range':
            return (
                <DataTableNumberRangeFilter
                    title={title}
                    value={value as { gte?: number; lte?: number } | undefined}
                    onChange={onChange}
                />
            );
        case 'text':
        case 'number':
            return (
                <DataTableTextFilter
                    title={title}
                    value={(value as string) || ''}
                    onChange={(newValue) => onChange(newValue || undefined)}
                    placeholder={config.placeholder}
                    type={config.type === 'number' ? 'number' : 'text'}
                />
            );
        default:
            return null;
    }
}
